import React, { useState, useEffect } from "react";
import { IGrid, IField } from "../../app/types";
import { StyleUtils } from "../../app/utils/styleUtils";
import {
  DataGrid,
  GridRenderCellParams,
  GridCellParams,
  MuiEvent,
} from "@mui/x-data-grid";
import { useAppSelector } from "../../app/hooks";
import { CheckBoxComponent } from "./index";
import { Component } from "./Component";
import { Box } from "@mui/material";

/* Functional component properties */
interface Props {
  classes: any;
  item: IGrid;
  clear: string;
  setClear: (clr: string) => void;
  buttonClickHandler: (button: IField) => void;
  linkClickHandler: (link: GridRenderCellParams) => void;
}

/* React Functional component */
export const DataGridComponent: React.FC<Props> = ({
  /* Function arguments (Props) */
  classes,
  item,
  clear,
  setClear,
  buttonClickHandler,
  linkClickHandler,
}): JSX.Element => {
  /* get state */
  const state = useAppSelector((state) => state);

  /* useState */
  const [columns, setColumns] = useState<IField[]>([]);
  const [rows, setRows] = useState([]);
  const [hiddenCols, setHiddenCols] = useState({});

  /* Compose hidden cols data */
  useEffect(() => {
    setColumns(
      state.mainContent.fieldList.filter((fld) => fld.grid === item.name)
    );

    var hideCols: any = {};
    state.mainContent.fieldList
      .filter((fld) => fld.grid === item.name)
      .forEach((col) => {
        hideCols[col.name] =
          col.visible === "false" // Either visibility is set in Columns header
            ? false
            : state.mainContent.fieldList.find(
                (fld) => fld.name === col.name && fld.rows?.length !== 0
              ) === undefined
            ? true
            : state.mainContent.fieldList.find(
                (fld) =>
                  fld.name === col.name &&
                  fld.rows?.find(
                    (row) => row.visible === undefined || row.visible === "true" // Or hide if all the rows for that column is visible = "false"
                  )
              )
            ? true
            : false;

        return;
      });

    setHiddenCols(hideCols);
  }, [state.mainContent.fieldList, state.mainContent.gridList]);

  /* Compose Rows data */
  useEffect(() => {
    var rowsData: any = [];
    var colsData = state.mainContent.fieldList.filter(
      (fld) => fld.grid && fld.grid === item.name
    );

    for (var i = 0; i < item.rowCount; i++) {
      var row: any = {
        id: i + 1,
      };
      colsData.forEach((col) => {
        row[col.name] = col.rows && col.rows[i] ? col.rows[i].value : "";
      });
      rowsData.push(Object.assign({}, row));
    }

    setRows(rowsData);
  }, [state.mainContent.fieldList, state.mainContent.gridList]);

  function renderHeader(header: any) {
    if (header.colDef.selectAll !== "") {
      return (
        <span className={classes.dataGridHeader}>
          <div>
            <strong>{header.colDef.headerName}</strong>
          </div>
          <div>
            <CheckBoxComponent
              classes={classes}
              item={header}
              isGridElem={true}
              setWidth={"true"}
              clear={clear}
              setClear={setClear}
            />
          </div>
        </span>
      );
    } else {
      return <strong>{header.colDef.headerName}</strong>;
    }
  }

  function renderCell(params: GridRenderCellParams<any>) {
    // console.log("Params: ", params);
    return (
      <Component
        classes={classes}
        item={params}
        isGridElem={true}
        clear={clear}
        setClear={setClear}
        buttonClickHandler={buttonClickHandler}
        linkClickHandler={linkClickHandler}
      />
    );
  }

  function getAlignment(type: string): "left" | "right" | "center" {
    switch (type) {
      // case "integer":
      //   return "right";

      case "boolean":
        return "center";

      default:
        return "left";
    }
  }

  return (
    // <Box style={{ height: 400, flexGrow: 1 }}>
    <Box className={classes.dataGrid}>
      <DataGrid
        className={classes.dataGridHeader}
        // getRowHeight={({ id, densityFactor }: GridRowHeightParams) => {
        //   return 50 * densityFactor;
        // }}
        // sx={{
        //   [`& .${gridClasses.cell}`]: {
        //     py: 0,
        //   },
        // }}
        getRowHeight={() => "auto"}
        getEstimatedRowHeight={() => 200}
        rowSpacingType="border"
        // autoHeight={true}
        // style={{
        //   flexGrow: 1,
        //   // border: "none", // For empty data grid
        // }}
        disableColumnSelector={true} // To hide the column visibility panel from the toolbar
        // isRowSelectable={() => false}
        // disableSelectionOnClick={true}
        // getRowClassName={(params) =>
        //   params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        // }

        onCellClick={(
          params: GridCellParams,
          event: MuiEvent<React.MouseEvent>
        ) => {
          event.defaultMuiPrevented = true;
        }}
        onCellKeyDown={(params, events) => events.stopPropagation()}
        columnVisibilityModel={hiddenCols} // Hide columns
        // headerHeight={0} // For empty data grid
        // rowHeight={0} // For empty data grid
        // hideFooter={true} // For empty data grid
        columns={columns.map((col) => {
          return {
            renderHeader: renderHeader, // For empty data grid
            field: col.name,
            headerName: col.label,
            value: col.value,
            type: col.type,
            rows: col.rows,
            maxWidth: col.type === "boolean" ? 100 : undefined,
            minWidth: Math.min(Number(StyleUtils.getWidth(col)), 100),
            // width: col.type === "boolean" ? 50 : 300,
            // width: 100,
            // maxWidth:
            //   col.type === "boolean" ? 100 : Number(StyleUtils.getWidth(col)),
            disableColumnMenu: true,
            length: col.length,
            renderCell: renderCell,
            flex: 1,
            // headerAlign: "center",
            align: getAlignment(col.type),
            clickable: col.clickable ? col.clickable : "false",
            keys: col.keys ? col.keys : [],
            function: col.function ? col.function : "",
            action: col.action ? col.action : "",
            selectAll: col.selectAll ? col.selectAll : "",
            sortable: col.type === "boolean" ? false : true,
            canEdit:
              state.mainContent.fieldList
                .filter((fld: IField) => fld.grid && fld.grid === item.name)
                .find(
                  (column) =>
                    col.name === column.name &&
                    column.rows !== undefined &&
                    column.rows?.find((row) => row.editable === "true") !==
                      undefined
                ) !== undefined
                ? true
                : false,
          };
        })}
        rows={rows}
        // rowsPerPageOptions={[5]}
        // pageSize={5}
      />
      {/* </Box> */}
    </Box>
  );
};
